// Modal on the native <dialog> (focus trap, Escape, top layer): a bottom sheet
// on phones, a centered panel on wider screens.
import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Visible heading; false keeps it for screen readers only. */
  showTitle?: boolean;
  description?: ReactNode;
  size?: 'sm' | 'md';
  children: ReactNode;
  footer?: ReactNode;
}

export function Dialog({ open, onClose, title, showTitle = true, description, size = 'md', children, footer }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('has-modal');
    } else if (!open && dialog.open) {
      dialog.close();
    }
    if (!open) document.documentElement.classList.remove('has-modal');
  }, [open]);

  useEffect(() => () => document.documentElement.classList.remove('has-modal'), []);

  return (
    <dialog
      ref={ref}
      className={`sheet sheet--${size}`}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onCloseRef.current();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onCloseRef.current();
      }}
    >
      {open && (
        <div className="sheet__panel">
          <div className="sheet__grabber" aria-hidden="true" />
          <header className="sheet__head">
            <h2 id={titleId} className={showTitle ? 'sheet__title' : 'sr-only'}>
              {title}
            </h2>
            <button type="button" className="icon-btn sheet__close" onClick={() => onCloseRef.current()} aria-label="Schließen">
              <X size={20} />
            </button>
          </header>
          {description && <p className="sheet__desc">{description}</p>}
          <div className="sheet__body">{children}</div>
          {footer && <footer className="sheet__foot">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}
